import React, {
  useState, useCallback, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import * as Sentry from '@sentry/node';

import Button from '../../atoms/button';
import Carousel from '../../atoms/carousel';
import IconCircleMinus from '../../atoms/icon-circle-minus';
import IconCirclePlus from '../../atoms/icon-circle-plus';
import ZoomSlide from '../../molecules/carousel-zoom';

const CarouselGrid = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  grid-area: product-carousel;
  max-width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    flex-direction: row;
  }
`;

const CarouselsWrapper = styled.section`
  padding-bottom: ${(props) => (props.multiImage ? '2rem' : '0')};
  overflow: hidden;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    ${(props) => (props.multiImage ? 'width: 85%' : 'min-width: 100%')};
    padding-bottom: 0;
    order: 2;
  }
`;

const CarouselLargeWrapper = styled.div`
  position: relative;
`;

const CarouselSmallWrapper = styled.div`
  cursor: grab;
  overflow-x: hidden;
  overflow-y: scroll;
  overflow: auto;
  padding-bottom: 20px;

  &::-webkit-scrollbar {
    width: 0px;
    background: transparent;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: flex;
    justify-content: center;
    max-height: 490px;
    overflow-x: initial;
    overflow-y: auto;
    padding-bottom: 0;
    order: 1;
  }
`;

const CarouselSmall = styled.ul`
  display: flex;
  flex-direction: row;

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: inline-flex;
    flex-direction: column;
  }
`;

const Thumbnail = styled.li`
  opacity: ${(props) => (props.active ? 1 : 0.6)};
  transition: opacity ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeInQuad};
  margin-right: 1.5rem;

  :last-of-type {
    margin-right: 0;
  }

  img {
    border-radius: 100%;
    border: 0.1rem solid ${(props) => props.theme.color.brand};
    display: block;
    height: 5rem;
    padding: 0.5rem;
    width: 5rem;
  }

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    margin-right: 0;
    margin-bottom: 1.5rem;

    :last-of-type {
      margin-bottom: 0;
    }
  }

  @media (min-width: ${(props) => props.theme.breakpoint.large}) {
    img {
      height: 7.5rem;
      width: 7.5rem;
    }
  }
`;

const sharedZoomButtonStyles = (props) => css`
  background: none;
  border: 0 none;
  height: 3rem;
  outline: none;
  padding: 0;
  stroke: ${props.theme.color.brand};
  width: 3rem;

  svg {
    fill: transparent;
    height: 3rem;
    stroke-width: 3;
    width: 3rem;
  }
`;

const CarouselZoom = styled.div`
  display: flex;
  height: 3rem;
  justify-content: center;
`;

const ZoomOut = styled(Button)`
  ${sharedZoomButtonStyles};
  margin-right: 0.5rem;
`;

const ZoomIn = styled(Button)`
  ${sharedZoomButtonStyles};
  margin-left: 0.5rem;
`;

const ProductCarousel = (props) => {
  const smallCarouselRef = useRef(null);

  const [carouselRef, setCarouselRef] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideImage, setSlideImage] = useState(props?.images[0]?.product_large_url ?? `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg`);
  const [zoomClosed, setZoomClosed] = useState(true);
  const [zoomOpened, setZoomOpened] = useState(false);
  const [zoomAnimating, setZoomAnimating] = useState(false);
  const [originalStyle] = useState(global.window?.getComputedStyle(global.document?.body).overflowY) ?? 'auto';

  const onCarouselRef = useCallback((node) => {
    if (node !== null) {
      setCarouselRef(node);
      node.on('change', (i) => {
        if (props.images[i]) {
          setSlideImage(props.images[i].product_large_url);
          setSlideIndex(i);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (zoomOpened) global.document.body.style.overflowY = 'hidden';
    if (zoomClosed) global.document.body.style.overflowY = originalStyle;
  }, [zoomOpened, zoomClosed]);

  const carouselSettings = {
    prevNextButtons: true,
    pageDots: false,
    wrapAround: true
  };

  const handleOnThumbnailClick = (i) => {
    if (carouselRef) carouselRef.selectCell(i);
  };

  const handleOnZoomOpenClick = () => {
    global.document.body.style.overflowY = 'hidden';

    setSlideImage(props.images[slideIndex].product_zoom_url);
    setZoomAnimating(true);

    setTimeout(() => {
      setZoomAnimating(false);
      setZoomClosed(false);
      setZoomOpened(true);
    }, 0); /* parseInt(theme.animation.default, 10) */
  };

  const handleOnZoomCloseClick = () => {
    global.document.body.style.overflowY = '';

    setZoomAnimating(true);

    setTimeout(() => {
      setZoomAnimating(false);
      setZoomClosed(true);
      setZoomOpened(false);
    }, 0); /* parseInt(theme.animation.default, 10) */
  };

  useEffect(() => {
    if (props.images.length === 0) {
      props.images.push({ product_zoom_url: `${process.env.NEXT_PUBLIC_ASSET_HOST}/images/default-image.jpg` });
    }

    setSlideImage(props.images[0].product_zoom_url);
  }, [props.images]);

  let smallCarouselPos = {
    top: 0, left: 0, x: 0, y: 0
  };

  const mouseMoveHandler = (e) => {
    const dx = e.clientX - smallCarouselPos.x;
    const dy = e.clientY - smallCarouselPos.y;

    if (smallCarouselRef?.current) {
      smallCarouselRef.current.scrollLeft = smallCarouselPos.left - dx;
      smallCarouselRef.current.scrollTop = smallCarouselPos.top - dy;
    }
  };

  const mouseUpHandler = () => {
    if (smallCarouselRef?.current) {
      smallCarouselRef.current.style.cursor = 'grab';
      smallCarouselRef.current.style.userSelect = 'user-select';
    }

    global.document.body.style.cursor = 'default';
    global.document.removeEventListener('mousemove', mouseMoveHandler);
    global.document.removeEventListener('mouseup', mouseUpHandler);
  };

  const mouseDownHandler = (e) => {
    if (smallCarouselRef?.current) {
      smallCarouselRef.current.style.cursor = 'grabbing';
      smallCarouselRef.current.style.userSelect = 'none';

      smallCarouselPos = {
        left: smallCarouselRef.current.scrollLeft,
        top: smallCarouselRef.current.scrollTop,
        x: e.clientX,
        y: e.clientY
      };
    }

    global.document.body.style.cursor = 'grabbing';
    global.document.addEventListener('mousemove', mouseMoveHandler);
    global.document.addEventListener('mouseup', mouseUpHandler);
  };

  useEffect(() => () => {
    global.document.removeEventListener('mousemove', mouseMoveHandler);
    global.document.removeEventListener('mouseup', mouseUpHandler);
  }, []);

  const handleOnImageLoad = (event, idx) => {
    const { performance } = global;
    const { currentTarget } = event;

    if (idx > 0
      || !currentTarget
      || !performance
      || !performance.clearMarks
      || !performance.mark
    ) return;

    try {
      performance.clearMarks('PDP Image');
      performance.mark('PDP Image');
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const createAlt = (index) => `${props.name} - ${props.productType} - ${index + 1}`;

  return (
    <CarouselGrid>
      {/* NOTE: because I need a buffer between grid and slider...😕 */}
      <CarouselsWrapper multiImage={props.images.length > 1}>
        <CarouselLargeWrapper>
          <Carousel
            flickityRef={onCarouselRef}
            type="pdp"
            options={carouselSettings}
          >
            {
              props.images.map(
                (image, idx) => (
                  <img
                    key={image.id}
                    src={slideIndex === idx ? slideImage : image.small_url}
                    alt={image.alt ?? `${createAlt(idx)}`}
                    onLoad={(e) => handleOnImageLoad(e, idx)}
                  />
                )
              )
            }
          </Carousel>

          <ZoomSlide
            image={slideImage}
            alt={createAlt(slideIndex)}
            onClose={handleOnZoomCloseClick}
            zoomAnimating={zoomAnimating}
            zoomClosed={zoomClosed}
            zoomOpened={zoomOpened}
          />
        </CarouselLargeWrapper>

        {props.images.length > 1 && (
          <CarouselZoom>
            <ZoomOut aria-label="zoom out product image" disabled><IconCircleMinus /></ZoomOut>
            <ZoomIn aria-label="zoom in product image"><IconCirclePlus onClick={handleOnZoomOpenClick} /></ZoomIn>
          </CarouselZoom>
        )}
      </CarouselsWrapper>

      {
        props.images.length > 1 && (
          <CarouselSmallWrapper
            ref={smallCarouselRef}
            onMouseDown={mouseDownHandler}
          >
            <CarouselSmall>
              {
                props.images.map((image, index) => (
                  <Thumbnail
                    key={image.id}
                    active={index === slideIndex}
                    onClick={() => handleOnThumbnailClick(index)}
                    tabIndex="0"
                    role="button"
                    aria-label={`${props.name} image thumbnail ${index + 1}`}
                    aria-pressed={index === slideIndex}
                  >
                    <img src={image.small_url} alt={image.alt ?? `${createAlt(index)} - thumbnail`} draggable="false" />
                  </Thumbnail>
                ))
              }
            </CarouselSmall>
          </CarouselSmallWrapper>
        )
      }
    </CarouselGrid>
  );
};

ProductCarousel.defaultProps = {
  productType: 'product'
};

ProductCarousel.propTypes = {
  images: PropTypes.array.isRequired,
  name: PropTypes.string.isRequired,
  productType: PropTypes.string
};

export default ProductCarousel;
